import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { templateService } from '../services/templateService'
import { TemplateCategory } from '../types/wordTemplate'
import { GenerateDocumentModal } from './GenerateDocumentModal'
import './TemplateSelectorModal.css'

interface TemplateSelectorModalProps {
  patientId: string
  consultationId?: string
  onClose: () => void
  onSuccess?: () => void
}

export function TemplateSelectorModal({
  patientId,
  consultationId,
  onClose,
  onSuccess,
}: TemplateSelectorModalProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates', selectedCategory],
    queryFn: () => templateService.getAll(selectedCategory === 'all' ? undefined : selectedCategory),
  })

  const filteredTemplates = templates?.filter((template) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      template.name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query) ||
      template.category.toLowerCase().includes(query)
    )
  })

  const formatCategoryName = (category: string): string => {
    return category
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId)
  }

  const handleGenerateSuccess = () => {
    onSuccess?.()
    onClose()
  }

  if (selectedTemplateId) {
    return (
      <GenerateDocumentModal
        templateId={selectedTemplateId}
        patientId={patientId}
        consultationId={consultationId}
        onClose={() => {
          setSelectedTemplateId(null)
          onClose()
        }}
        onSuccess={handleGenerateSuccess}
      />
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content template-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Template</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="template-selector-filters">
            <div className="filter-group">
              <label htmlFor="category-filter">Category</label>
              <select
                id="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as TemplateCategory | 'all')}
                className="form-control"
              >
                <option value="all">All Categories</option>
                <option value="letter">Letter</option>
                <option value="referral">Referral</option>
                <option value="prescription">Prescription</option>
                <option value="certificate">Certificate</option>
                <option value="form">Form</option>
                <option value="report">Report</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="search-filter">Search</label>
              <input
                id="search-filter"
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-control"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="template-selector-loading">
              <p>Loading templates...</p>
            </div>
          ) : !filteredTemplates || filteredTemplates.length === 0 ? (
            <div className="template-selector-empty">
              <p>No templates found.</p>
              {searchQuery && <p className="hint">Try adjusting your search or category filter.</p>}
            </div>
          ) : (
            <div className="template-selector-list">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="template-selector-item"
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <div className="template-item-header">
                    <h3>{template.name}</h3>
                    <span className={`template-category category-${template.category}`}>
                      {formatCategoryName(template.category)}
                    </span>
                  </div>
                  {template.description && (
                    <p className="template-item-description">{template.description}</p>
                  )}
                  {template.variables && template.variables.length > 0 && (
                    <div className="template-item-variables">
                      <span className="variables-count">
                        {template.variables.length} variable{template.variables.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}






























